'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { TrailerGate } from '@cineamo/api/models/movie-trailer/TrailerPlayback.types';

import { resolveTrailerPlayback } from '@cineamo/api/helpers/trailer-playback-helper';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useLegalClock } from '../hooks/useLegalClock';

export type TrailerUnavailableNoticeProps = {
    className?: string;
    gate: TrailerGate;
};

/**
 * Shown in place of the player when {@link resolveTrailerPlayback} blocks embedded playback under
 * § 5 Abs. 4 JMStV (CIN-14724).
 *
 * Explains rather than hides: the play button stays live and the viewer is told when the trailer
 * becomes available, instead of hitting a dead control with no reason given.
 *
 * No link out to YouTube — a product decision; the copy is the whole of it.
 *
 * It recomputes the decision rather than taking a boolean prop, so that the window boundary and the
 * copy describing it can never disagree.
 */
export const TrailerUnavailableNotice: React.FC<TrailerUnavailableNoticeProps> = ({ className, gate }) => {
    const t = useTranslations();
    // Same server clock the blocking decision was taken on, so the window boundary and the copy
    // describing it can never disagree.
    const clock = useLegalClock(gate);

    const { embeddableFrom, effectiveAgeRating, isOwnAgeRating } = resolveTrailerPlayback(
        gate.rating,
        clock,
        gate.countryCode
    );

    // The rating is only named when it is the TRAILER's own, and only when it says something: a
    // borrowed film rating would state a Freigabe this trailer does not have (see TrailerAgeRating),
    // and an empty string would render "trailers rated  and over".
    const namedAgeRating = isOwnAgeRating && effectiveAgeRating ? effectiveAgeRating : undefined;

    const description = (() => {
        // `embeddableFrom` is a German wall-clock reading, "22:00". Shown as given rather than
        // reformatted per locale: it is a time on the German clock the law names, not a time in the
        // viewer's own day, and every German-language variant of this copy writes "ab 22:00 Uhr".
        if (embeddableFrom && namedAgeRating) {
            return t('movie.trailer-blocked-description-rated', {
                ageRating: namedAgeRating,
                time: embeddableFrom
            });
        }
        if (embeddableFrom) {
            return t('movie.trailer-blocked-description-time', { time: embeddableFrom });
        }
        // No opening time to name — the server clock has not arrived yet, or could not be read.
        return t('movie.trailer-blocked-description');
    })();

    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center gap-12 size-full px-24 py-16 text-center bg-black',
                className
            )}>
            <p className="text-body-bold-14 text-white lg:text-body-bold-16">{t('movie.trailer-blocked-title')}</p>
            <p className="max-w-[46ch] text-body-12 text-white/70 lg:text-body-14">{description}</p>
        </div>
    );
};

export default TrailerUnavailableNotice;
